// #region imports
import {
  addSections,
  resetSections,
} from '../../../../sections/redux/sectionsSlice';
import {
  addWindows,
  resetWindows,
} from '../../../../windows/redux/windowsSlice';
import sessionStorageSingleton from '../../../configs/sessionStorageSingleton';
import { getKeyPair, encryptPrivateKey } from '../../../cryptor/keyPairCrypto';
import { closeGlobalDBs } from '../../../database/manager/databaseManager';
import {
  getUserPublicKey,
  saveUser,
} from '../../../database/user/multiUserStorage';
import { UserUIData, loadUserUiData } from '../../../database/user/userStorage';
import { tabSharedWorker } from '../../../sharedWorker/sharedWorkerManager';
import { SharedWorkerMessage } from '../../../sharedWorker/types';
import { ResultWithOptionalError } from '../../../types/generic/results';
import {
  saveUserMetaDataLS,
  saveAppConfigsLS,
  removeUserMetaDataLS,
  getManualModeState,
} from '../../../utils/auth/authUtils';
import { showErrorToast } from '../../../utils/view/toast';
import { AppDispatch, GetState } from '../../store';
import { authAppInitialState, addAuth, removeAuthUser } from '../authSlice';
import {
  AppConfigs,
  AppData,
  PrimaryStorage,
  UserData,
  UserMetaData,
} from '../authTypes';
import {
  getUserGroupsDBName,
  getUserSheetsIdToNameDBName,
} from '../../../../sheet/databaseDeprecated/helpers';
import {
  SheetStorageWorkerParams,
  SnapshotType,
} from '../../../../sheet/databaseDeprecated/types';
import { UserKeyStore } from '../../../database/schemas';
import {
  initializeSheetsState,
  resetSheetsState,
} from '../../../../sheet/redux/sheetsManagerSlice';
import { SheetManagerSliceState } from '../../../../sheet/redux/types';
import { readEntityIdToNameMap } from '../../../database/user/userEntityInfo';
const { MasterFeKey, UserFeKey } = UserKeyStore;
// #endregion

// #region logout user
// Logout user will also work as switch user
interface LogoutUserParams {
  navigateAuth: () => void;
  newUserMetaData?: UserMetaData;
  forceLogout?: boolean;
}
export const logoutUser =
  ({ navigateAuth, newUserMetaData, forceLogout }: LogoutUserParams) =>
  async (dispatch: AppDispatch, getState: GetState) => {
    navigateAuth();
    const userId: string = getState().auth.user.id;
    const isMasterTab: boolean = !!getState().auth.app.isMasterTab;

    tabSharedWorker.port.postMessage({
      type: SharedWorkerMessage.ShiftMasterTab,
      userId,
    });

    if (isMasterTab && newUserMetaData) {
      saveUserMetaDataLS(newUserMetaData);
    } else if ((!newUserMetaData || isMasterTab) && !forceLogout) {
      // In case of force logout data is already removed by the calling tab
      removeUserMetaDataLS();
      tabSharedWorker.port.postMessage({
        type: SharedWorkerMessage.UserLogout,
        userId,
      });
    }

    closeGlobalDBs();
    dispatch(removeAuthUser(newUserMetaData));
    dispatch(resetWindows());
    dispatch(resetSections());
    dispatch(resetSheetsState());
    // removeUserMetaDataLS();
    sessionStorageSingleton.reset();
    // Navigate back to auth
  };
// #endregion
// #region initialize user
interface InitializeUser {
  userMetaData: UserMetaData;
  appConfigs: AppConfigs;
  privateKey: CryptoKey;
  // feKey?: CryptoKey;
  unSavedSheets?: SheetManagerSliceState;
  directoryHandler?: FileSystemDirectoryHandle;
}
export const initializeUser =
  (payload: InitializeUser) =>
  async (dispatch: AppDispatch, getState: GetState) => {
    const { userMetaData, appConfigs, privateKey, directoryHandler } = payload;
    const userId = userMetaData.id;
    const primaryStorage = appConfigs.primaryStorage;

    const state = getState();
    const isUserAlreadyExists = !!state.auth.user.id.length;
    if (isUserAlreadyExists) return;

    // Initialize data
    dispatch(
      addAuth({
        app: appConfigs,
        user: userMetaData,
      })
    );

    const pubKeyResult = await getUserPublicKey(userMetaData.id);
    if (pubKeyResult.error || !pubKeyResult.data)
      return showErrorToast(pubKeyResult.error?.message);

    sessionStorageSingleton.initialize(
      userId,
      privateKey,
      pubKeyResult.data,
      directoryHandler
    );

    // Set keys
    let masterFeKey = undefined; // used for encrypting common data between users
    let userFeKey = undefined; // Will be used for encrypting windows ui and groups
    if (privateKey) {
      masterFeKey = await sessionStorageSingleton.getFeKey(MasterFeKey);
      userFeKey = await sessionStorageSingleton.getFeKey(UserFeKey);
    }
    if (!masterFeKey || !userFeKey) return;

    const entityIdToNameMap = await readEntityIdToNameMap(userId, userFeKey);

    // console.log('entityIdToNameMap', entityIdToNameMap);

    // Load Groups
    // const groupsAndSheetNamesResult: ResultWithOptionalError<ReadGroupsAndSheetsIdToNameResult> =
    //   await readGroupsAndSheetsIdToName(userId, primaryStorage);
    // if (groupsAndSheetNamesResult.error || !groupsAndSheetNamesResult.data)
    // return showErrorToast('Error reading groups and sheetsIdToName');

    const result: ResultWithOptionalError<UserUIData> = await loadUserUiData(
      userId,
      userFeKey
    );

    if (result.error) {
      return showErrorToast(result.error.message);
    }

    // console.log('updatedGroups', updatedGroups);
    // console.log('updatedSheets', updatedSheets);

    // console.log('unSavedSheets', payload.unSavedSheets);
    // console.log('getManualModeState(userId)', getManualModeState(userId));

    if (result.data) {
      dispatch(
        initializeSheetsState({
          // history: result.data.sheetsHistory,
          isManualSave: !!getManualModeState(userId),
          unSavedChanges: payload.unSavedSheets?.unSavedChanges, // TODO: Replace this with actual new changesToBeSynced
          unSavedHistory: payload.unSavedSheets?.unSavedHistory,
          entityIdToNameMap,
          // groups: groupsAndSheetNamesResult.data?.groups,
        })
      );
      dispatch(
        addWindows({
          windows: result.data.windows ?? [],
          activeWindowId: result.data.activeWindowId,
        })
      );
      dispatch(addSections(result.data.sections ?? {}));
    }
  };
// #endregion

// #region login user
interface LoginUser extends Pick<AppConfigs, 'primaryStorage'> {
  storageSelected: boolean;
  navigateHome: () => void;
  userMetaData: UserMetaData;
  directoryHandler?: FileSystemDirectoryHandle;
}
export const loginUser =
  (payload: LoginUser) => async (dispatch: AppDispatch) => {
    const {
      userMetaData,
      primaryStorage,
      storageSelected,
      directoryHandler,
      navigateHome,
    } = payload;

    const userId = userMetaData.id;

    const appData: AppData = { ...authAppInitialState };
    let err: string | undefined = '';

    // Save Directory Handler
    if (!storageSelected && primaryStorage === PrimaryStorage.FileSystem) {
      appData.primaryStorage = primaryStorage;
    }

    // Generate User Keys
    const keys: CryptoKeyPair = await getKeyPair();
    // Set Keys
    sessionStorageSingleton.initialize(
      userId,
      keys.privateKey,
      keys.publicKey,
      directoryHandler
    );

    // Master Key Will be received from server
    const feKey: CryptoKey | undefined =
      await sessionStorageSingleton.generateAndSaveFeKey(MasterFeKey);
    if (!feKey) return;

    // Enc Key And Save User
    const encPrivateKey: string = await encryptPrivateKey(
      keys.privateKey,
      // '123'
      userId
    );

    err = await startSaveUser(userMetaData, encPrivateKey, keys.publicKey);
    if (err) return;

    const userFeKey: CryptoKey | undefined =
      await sessionStorageSingleton.generateAndSaveFeKey(UserFeKey);
    if (!userFeKey) return;
    // Save Groups
    saveUserGroupsAndSheetsIdToName(
      userFeKey,
      primaryStorage,
      directoryHandler
    );

    // const exportedFeKey: string = yield exportFeKey(feKey);
    const user: UserData = {
      ...userMetaData,
      // exportedFeKey,
    };

    saveUserMetaDataLS(user);
    saveAppConfigsLS(appData);
    dispatch(addAuth({ user, app: appData }));
    navigateHome();
  };

//   #endregion

// #region helpers
function saveUserGroupsAndSheetsIdToName(
  feKey: CryptoKey,
  primaryStorage: PrimaryStorage,
  directoryHandle?: FileSystemDirectoryHandle
) {
  const params: SheetStorageWorkerParams = {
    feKey,
    dbName: getUserGroupsDBName(),
    primaryStorage,
    snapshotParams: {
      groups: {},
      snapshotType: SnapshotType.Groups,
    },
    directoryHandle,
  };

  // sheetSharedStorageWorker.port.postMessage(params);
  params.dbName = getUserSheetsIdToNameDBName();
  params.snapshotParams = {
    snapshotType: SnapshotType.EntityIdToName,
    sheetsIdToName: {},
  };
  // sheetSharedStorageWorker.port.postMessage(params);
}

export async function startSaveUser(
  user: UserMetaData,
  encPrivateKey: string,
  publicKey: CryptoKey
) {
  const result: ResultWithOptionalError<boolean> = await saveUser(
    user,
    encPrivateKey,
    publicKey
  );
  if (result.error) {
    showErrorToast(result.error.message);
    return result.error.message;
  }
}
// #endregion
